import $ from 'jquery';

import ChoiceTreeAutocomplete from 'chairisher/component/autocomplete/choicetree';
import ProductUpdateCard from 'chairisher/view/product/update/card';

import { getProductObjectValueByKey } from 'chairisher/context/product';
import {
    getChoiceTreeEndpointByFieldName,
    getTreeChoicesByFieldName,
    getProductObject,
} from 'chairisher/context/listingflow';
import { wrapTextWithParenthesis } from 'chairisher/util/format';

/**
 * View that encapsulates functionality for the "Details" section of the Product listing flow.
 */
class DetailsCard extends ProductUpdateCard {
    constructor() {
        super('#js-details-fields');

        /**
         * Maps brand field name to Autocomplete instance
         *
         * @type {Array.<ChoiceTreeAutocomplete>}
         */
        this.brandFieldNameToAutocompleteMap = {};
        /**
         * A collection of field names that are bound as ChoiceTreeAutocomplete components
         *
         * @type {Array.<string>}
         */
        this.choiceTreeComponentNames = [
            'art_subjects',
            'primary_color_code',
            'colors',
            'materials',
            'styles',
            'tile_usage',
        ];
        this.brandFieldNametoBrandTypeKeyMap = {
            artists: 'A',
            brands: 'B',
            designers: 'D',
            styled_after: 'S',
        };

        this.brandTypeKeytoBrandFieldNameMap = {};
        $.each(this.brandFieldNametoBrandTypeKeyMap, (k, v) => {
            this.brandTypeKeytoBrandFieldNameMap[v] = k;
        });

        /**
         * A collection of field names that are bound as BooleanInput components
         *
         * @type {Array.<string>}
         */
        this.booleanInputComponentNames = [
            'period_made',
            'used_code',
            'alterations_code',
            'made_in_country_code',
            'imperfections_code',
            'working_condition',
        ];

        /**
         * A collection of field names that are bound as ImageSelect components
         *
         * @type {Array.<string>}
         */
        this.imageSelectComponentNames = ['plug_type'];

        /**
         * Mapping of parent field names to more readable display labels
         *
         * @type {Object.<string, string>}
         */
        this.imageSelectLabelDisplayMap = { plug_type: 'Plug Type' };

        /**
         * A collection of field names that are bound as MultiSelectCheckbox components
         *
         * @type {Array.<string>}
         */
        this.multiSelectCheckboxComponentNames = ['power_sources'];

        /**
         * Mapping of plug type strings to their region strings
         *
         * @todo CHAIR-12559: this will eventually be removed and consumed from the backend
         * @type {Object.<string, string>}
         */
        this.plugTypeToRegionMap = {
            // @todo CHAIR-12559: this will eventually be removed and consumed from the backend
            'Type A': 'USA, MEX, CAN, JPN',
            'Type B': 'USA, MEX, CAN, JPN',
            'Type C': 'Europe, South America, Asia',
            'Type D': 'IND',
            'Type E': 'FRA, BEL, POL, SVK, CZE',
            'Type F': 'Europe, RUS',
            'Type G': 'GBR, IRL, MLT, MYS, SGP',
            'Type H': 'ISR',
            'Type I': 'AUS, ARG, CHN, NZL',
            'Type J': 'CHE, LIE, RWA',
            'Type K': 'DNK, GRL',
            'Type L': 'ITA, CHL',
            'Type M': 'ZAF',
            'Type N': 'BRA, ZAF',
            'USB': '',
        };

        /**
         * Relational object pairing the selected parent field with the target parent field, as well as their input values
         *
         * @type {Object.<string, string>}
         */
        this.selectedDisabledInputs = {
            alterations_code: { working_condition: { YES_PR: ['U'] } },
            working_condition: { alterations_code: { U: ['YES_PR'] } },
            period_made: { made_in_country_code: { UN: ['NOT_MADE', 'P2020_2029', 'P2010_2019', 'P2000_2009'] } },
            made_in_country_code: {
                period_made: {
                    NOT_MADE: ['UN'],
                    P2020_2029: ['UN'],
                    P2010_2019: ['UN'],
                    P2000_2009: ['UN'],
                },
            },
        };

        /**
         * A collection of objects whose nested values represent the parent field names to be selected
         * The child inputs of those parent fields will later have their disabled inputs cleared based on the selection of the key
         *
         * @type {Array.<object>}
         */
        this.selectedDisabledResets = [
            { power_sources: { NONE: ['alterations_code'] } },
            { period_made: { NOT_MADE: ['alterations_code'] } },
            { used_code: { N: ['alterations_code'] } },
        ];

        /**
         * A collection of objects with the parent key representing the initiating field section
         * The nested object displays the relationship between the parent's input and a separate, targeted field
         *
         * @type {Array.<object>}
         */
        this.selectedFieldsByFieldInput = [{ power_sources: { CORD: ['plug_type'] } }];

        /**
         * A collection of objects with the key representing the initiating field section
         * The object's values represent separate fields to take action upon
         *
         * @type {Array.<object>}
         */
        this.selectedFieldsByFieldName = [
            { power_sources: ['voltage', 'plug_type', 'working_condition'] },
            { period_made: ['working_condition'] },
            { used_code: ['working_condition'] },
        ];

        /**
         * Mapping of parent field names to child input value to use for when to initiate action upon other fields
         *
         * @type {Object.<string, string>}
         */
        this.selectedFieldsByFieldNameKeyMap = {
            power_sources: 'NONE',
            period_made: 'NOT_MADE',
            used_code: 'N',
        };

        /**
         * Relational object pairing the selected parent field with the target parent field, as well as their input values
         *
         * @type {Object.<string, string>}
         */
        this.selectedHiddenInputs = {
            used_code: {
                imperfections_code: {
                    Y: 'N',
                    N: 'L',
                },
            },
        };
        /**
         * Mapping of ui-only field name to real field name to use for disabling fields
         *
         * @type {Object.<string, string>}
         */
        this.uiOnlyFieldNameToRealFieldNameMap = {
            is_set: 'lot_size',
        };

        /**
         * Used to traverse the DOM to select the card's completion indicator
         *
         * @type {string}
         */
        this.completionIndicatorSelector = '.js-step-details';

        /**
         * Regular expression used to swap out hex for an HTML color swatch in the summary
         *
         * @type {RegExp}
         */
        this.summaryColorKeyRegex = /__([0-9a-fA-F]{6})__/g;

        this.initialize();
    }

    /**
     * Builds the text containers for displaying the selected values and inserts them above the list of options / below section label
     *
     * @param {string} fieldName The name of the field section where the newly built text field will be inserted (e.g. plug_type)
     */
    buildImageSelectTextContainers(fieldName) {
        const fieldNameKebabCase = fieldName.replace(/_/g, '-');
        const textContainerId = `${fieldNameKebabCase}-field-text-container`;

        const $textContainer = $('<div></div>', {
            id: textContainerId,
            class: 'image-select-field-text-container',
            text: this.getDefaultImageSelectText(fieldName),
        });

        const $optionsList = $(`.${fieldName}`).find('ul');
        $textContainer.insertBefore($optionsList);

        this.bindImageSelectListToggles(textContainerId, $optionsList);
    }

    /**
     * Locates the targeted image select labels and rebuilds them with custom html to allow for styling
     *
     * @param {string} fieldName The name of the field section where the specific list items are to be found (e.g. plug_type)
     */
    rebuildImageSelectLabels(fieldName) {
        const existingValue = getProductObjectValueByKey(fieldName);
        const $options = $(`.${fieldName}`).find('li');

        if ($options.length) {
            $options.each((i, option) => {
                const $option = $(option);
                const $input = $option.find('input');

                let iconHTML = '<span class="cicon"></span>';
                if ($input.length) {
                    // dynamically generates icons
                    const inputValue = $input.val();
                    const iconType = fieldName.replace('_', '');
                    iconHTML = `<span class="cicon cicon-${iconType}-${inputValue.toLowerCase()}-stroke"></span>`;

                    // adds a border to selection if persisted from previous save
                    if (inputValue === existingValue) {
                        $option.addClass('selected');
                    }
                }

                const $choiceText = $option.find('.js-choice-text');
                const label = $choiceText.text();
                const titleHTML = `<p class="js-label-title label-title">${label} </p>`;

                const region = this.plugTypeToRegionMap[label];
                const subtitleHTML = `<p class="js-label-subtitle label-subtitle">${region}</p>`;
                // @todo CHAIR-12559: this will eventually be updated to consume region strings from the backend

                $choiceText.html(iconHTML + titleHTML + subtitleHTML);
            });
        }
    }

    /**
     * Sets the brand widget (artist/brand/maker/styled after) with the given values
     * @param {Array.<Choice>} brandValues
     */
    setBrandValues(brandValues) {
        if (Array.isArray(brandValues)) {
            if (brandValues.length === 1 && this.isBrandValueUnknown(brandValues[0])) {
                $('.js-brand-unknown').prop('checked', true);
                $('#js-brands-wrapper').collapse('hide');
            } else {
                for (let i = 0; i < brandValues.length; i += 1) {
                    const b = brandValues[i];
                    const fieldName = this.brandTypeKeytoBrandFieldNameMap[b.brand_type.value];
                    const autocomplete = this.brandFieldNameToAutocompleteMap[fieldName];
                    autocomplete.addChoice(b);
                }
            }
        }
    }

    /** @inheritdoc * */
    updateFieldsFromDataObject(data) {
        super.updateFieldsFromDataObject(data);

        //
        // Brands
        //
        const brandData = data.brands;
        if (brandData) {
            $.each(this.brandFieldNameToAutocompleteMap, (name, autocomplete) => {
                if (brandData.is_readonly) {
                    autocomplete.disable();
                } else {
                    autocomplete.enable();
                }
            });

            $('.js-brand-unknown').prop('disabled', !!brandData.is_readonly);
        }
    }

    /** @inheritdoc */
    updateWarningsFromDataObject(data) {
        //
        // Dimensions
        //

        let dimensionWarnings = [];

        $.each(['dimension_depth', 'dimension_height', 'dimension_width'], (_, dimension) => {
            if (dimension in data) {
                dimensionWarnings = dimensionWarnings.concat(data[dimension].warnings || []);
            }
        });

        this.updateFieldWarnings($('.js-dimension-warnings-wrapper'), dimensionWarnings);
    }

    /**
     * Binds actions to the various elements that comprise the Card
     */
    bind() {
        super.bind();

        //
        // Bind brand Autocompleters
        //

        $.each(this.brandFieldNametoBrandTypeKeyMap, (name) => {
            this.bindBrandWidget(name);
        });

        //
        // Bind Unknown Checkbox
        //
        const $brandUnknown = $('.js-brand-unknown');

        $('#js-brands-wrapper').on('shown.bs.collapse hidden.bs.collapse', () => {
            const data = getProductObject();

            if (data.brands && !data.brands.is_readonly) {
                $brandUnknown.prop('disabled', false);
            }
        });

        $brandUnknown.on('change', (e) => {
            $brandUnknown.prop('disabled', true);

            const isChecked = $(e.target).is(':checked');

            $('#js-brands-wrapper')
                .stop()
                .collapse(isChecked ? 'hide' : 'show');

            $.each(this.brandFieldNameToAutocompleteMap, (name, autocomplete) => {
                autocomplete.removeAllChoices();
            });

            this.triggerChange({
                brands: isChecked ? this.getBrandValueUnknown() : this.getBrandValue(),
            });
        });

        //
        // Bind BooleanInput components
        //

        this.booleanInputComponentNames.forEach((fieldName) => {
            this.bindBooleanInputs({
                $queryEl: this.$cardFieldsEl.find(`[name="${fieldName}"]`),
            });
        });

        //
        // Bind ImageSelect components
        //

        this.imageSelectComponentNames.forEach((fieldName) => {
            $(`.${fieldName}`).addClass('image-select-field');

            this.bindImageSelectComponentNames({
                $queryEl: this.$cardFieldsEl.find(`[name="${fieldName}"]`),
            });
        });

        //
        // Bind MultiSelectCheckbox components
        //

        this.multiSelectCheckboxComponentNames.forEach((fieldName) => {
            this.bindMultiSelectCheckboxes({
                $queryEl: this.$cardFieldsEl.find(`[name="${fieldName}"]`),
            });
        });

        //
        // Bind ChoiceTreeAutocomplete components
        //

        this.choiceTreeComponentNames.forEach((fieldName) => {
            let dataDisplayKey = 'taxonomy';

            if ($.inArray(fieldName, ['art_subjects', 'tile_usage']) !== -1) {
                dataDisplayKey = 'display';
            }

            this.bindChoiceTreeAutocomplete({
                dataDisplayKey,
                dataEndpoint: getChoiceTreeEndpointByFieldName(
                    fieldName === 'primary_color_code' ? 'colors' : fieldName,
                ),
                isSingleSelect: fieldName === 'primary_color_code',
                treeChoices: getTreeChoicesByFieldName(fieldName),
                $queryEl: this.$cardFieldsEl.find(`[name="${fieldName}"]`),
            });
        });

        const $requiresProp65WarningField = $(this.cardSelector).find('.js-requires-prop-65-warning');
        const $prop65WarningField = $(this.cardSelector).find('.js-prop-65-warning');
        if ($requiresProp65WarningField.length && $prop65WarningField.length) {
            $requiresProp65WarningField.removeClass('hidden');

            $requiresProp65WarningField.on('change', (e) => {
                if (e.target.value === 'Y') {
                    $prop65WarningField.removeClass('hidden');
                } else {
                    $prop65WarningField.val('');
                    $prop65WarningField.addClass('hidden');
                }
            });

            $requiresProp65WarningField.find('.cicon-info').on('click', (e) => {
                e.preventDefault();
                $('body').addClass('static-modal');
                $('.js-modal-prop-65-info').modal('show');
            });

            $('.js-modal-prop-65-info').on('hidden.bs.modal', () => {
                $('body').removeClass('static-modal');
            });
        }
    }

    /**
     * Instantiates and binds actions to a text input that allows selection of a brand given a field name and brand type
     *
     * @param {string} fieldName The name of the field
     */
    bindBrandWidget(fieldName) {
        const $queryEl = this.$cardFieldsEl.find(`[name="${fieldName}"]`);

        this.brandFieldNameToAutocompleteMap[fieldName] = new ChoiceTreeAutocomplete({
            $queryEl,
            dataEndpoint: '/choices/product/brands/search',
            menuChoiceHasThumbnail: false,
        });

        $queryEl.on('choicetree:change', () => {
            this.triggerChange({ brands: this.getBrandValue() });
        });
    }

    /**
     * Attaches event listeners for toggling the display of the section containing the list items
     *
     * @param {string} textContainerId The id of the text container to which an event listener will be attached
     * @param {jQuery} $optionsList The ul container to hide or show; also, contains the list items to which event listeners will be attached
     */
    bindImageSelectListToggles(textContainerId, $optionsList) {
        $optionsList.addClass('hidden'); // hides dropdown by default

        // toggles dropdown when clicking on the text container
        $(`#${textContainerId}`).on('click', () => {
            $optionsList.toggleClass('hidden');
        });

        // closes dropdown when clicking on an image item
        $optionsList.find('li').on('click', () => {
            $optionsList.addClass('hidden');
        });
    }

    /**
     * @returns {Array.<Choice>} The brand field's values
     */
    getBrandValue() {
        const vals = [];

        $.each(this.brandFieldNameToAutocompleteMap, (brandFieldName, autocomplete) => {
            const brandTypeKey = this.brandFieldNametoBrandTypeKeyMap[brandFieldName];

            if (autocomplete) {
                $.each(autocomplete.getValue(), (i, v) => {
                    vals.push({
                        value: v,
                        brand_type: {
                            value: brandTypeKey,
                        },
                    });
                });
            }
        });

        return vals;
    }

    /**
     * The default value for when the artist/brand/maker/"styled after" is unknown
     * @returns {Array.<Choice>}
     */
    getBrandValueUnknown() {
        return [
            {
                brand_type: {
                    value: 'B',
                },
                value: 'Unknown',
            },
        ];
    }

    /**
     * Determines the default text to pass along to the image select text container to display at first load
     * @todo CHAIR-12559: this will likely need to be refactored once plug type region is consumed from the backend
     *
     * @param {string} fieldName The name of the field section where the text will be displayed (e.g. plug_type)
     * @returns {string} The text to display (e.g. "Choose Plug Type" or "Type A...")
     */
    getDefaultImageSelectText(fieldName) {
        let text = `Choose ${this.imageSelectLabelDisplayMap[fieldName]}`;

        const existingValue = getProductObjectValueByKey(fieldName);
        if (existingValue && fieldName === 'plug_type') {
            let label = existingValue;
            if (existingValue.length === 1) {
                label = `Type ${existingValue}`;
            }

            text = `${label} ${wrapTextWithParenthesis(this.plugTypeToRegionMap[label])}`;
        }

        return text;
    }

    /**
     *
     * @param {Choice} choice The choice to check for equality with the unknown choice
     * @returns {boolean} True indicates the given choice is Brand Unknown, false otherwise
     */
    isBrandValueUnknown(choice) {
        const unknown = this.getBrandValueUnknown()[0];

        if (choice.brand_type && choice.brand_type.value) {
            return !!(unknown.brand_type.value === choice.brand_type.value && unknown.value === choice.value);
        }
        return false;
    }

    /**
     * Marshals a jQuery element that represents a line in the card summary
     *
     * @param {string} label The label to display
     * @param {string} values The values to display
     * @returns {jQuery}
     */
    marshalSummaryLineEl(label, values) {
        let display = values;
        if (label && label.toLowerCase() === 'features') {
            const matches = display.match(this.summaryColorKeyRegex);
            if (matches) {
                for (let i = 0; i < matches.length; i += 1) {
                    const match = matches[i];
                    const hex = match.replace(/_/g, '');

                    const swatch = $('<span></span>', {
                        class: 'swatch',
                        style: `background-color: #${hex}`,
                    })[0].outerHTML;

                    display = display.replace(match, swatch);
                }
            }
        }

        return super.marshalSummaryLineEl(label, display);
    }

    /**
     * Populates the list of simple field names excluding buttons
     */
    populateSimpleFieldNames() {
        this.simpleFieldNames = $.makeArray(
            $(this.fieldNames)
                .not(this.choiceTreeComponentNames)
                .not(Object.keys(this.brandFieldNametoBrandTypeKeyMap))
                .not(Object.keys(this.uiOnlyFieldNameToRealFieldNameMap)),
        );
    }
}

export default DetailsCard;
